/**
 * Verdict layer for the seeding calculator polish. Wraps the existing
 * `calculateSeedingWindows` engine in a 5-state verdict the live preview
 * card uses to drive its headline, color, CTA, and reminder copy.
 *
 * - 'go'           — in primary optimal window
 * - 'go-secondary' — in secondary optimal window (spring backup for cool-season)
 * - 'closing'      — ≤10 days left in current optimal window, OR past optimal but inside acceptable
 * - 'soon'         — next window opens in ≤21 days, prep now
 * - 'too-late'     — past this season and next window is >60 days away
 * - 'wait'         — between 21 and 60 days from the next window
 *
 * Verdict is derived from primary/secondary window dates relative to `today`
 * rather than the engine's `status` field, because the engine overloads
 * `'too_late'` to mean both "genuinely past everything" and "waiting for the
 * next year's window" — collapsing that lets the UI pick the right copy.
 */

import { buildSeedingCalendar } from "./seeding-month-grid"
import type { SeedingResult, SeedingWindow } from "./seeding-calculator"
import type { YearCalendarCell } from "./year-calendar-types"

export type SeedingVerdict =
  | "go"
  | "go-secondary"
  | "closing"
  | "soon"
  | "too-late"
  | "wait"

export type SeedingCtaMode = "calendar" | "remind" | "none"

export interface SeedingViewPlan {
  verdict: SeedingVerdict
  /** Short uppercase pill label, e.g. "Plant now". */
  pillLabel: string
  /** Main headline, e.g. "Plant now — you're in the peak window". */
  headline: string
  /** One-sentence detail under the headline. */
  detail: string
  /** Which CTA to render under the preview. */
  ctaMode: SeedingCtaMode
  /** Date for "Remind me" CTA — 14 days before the next window opens. */
  reminderDate: Date | null
  /** Days remaining in the current optimal window, when applicable. */
  daysLeft: number | null
  /** Days until the next optimal window opens, when applicable. */
  daysToOpen: number | null
  /** The window the preview card headlines (current or upcoming). */
  bestWindow: SeedingWindow | null
  /** Whether the headline window is the primary or secondary occurrence. */
  bestWindowKind: "primary" | "secondary" | null
  /** 12-month strip cells for the calendar. */
  months: YearCalendarCell[]
  /** 0-indexed month of bestWindow.optimalStart, for the NOW/best ring. */
  bestMonth: number | undefined
  /** Soil-temp target string, e.g. "50–65°F". */
  soilTemp: string
  /** Germination time string, e.g. "7–14 days". */
  germDays: string
}

const MONTH_LABEL = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]

const DAY_MS = 86400000

function daysBetween(from: Date, to: Date): number {
  return Math.ceil((to.getTime() - from.getTime()) / DAY_MS)
}

export function fmtMonthDay(date: Date): string {
  return `${MONTH_LABEL[date.getMonth()]} ${date.getDate()}`
}

export function fmtRange(start: Date, end: Date): string {
  if (start.getMonth() === end.getMonth()) {
    return `${MONTH_LABEL[start.getMonth()]} ${start.getDate()}–${end.getDate()}`
  }
  return `${fmtMonthDay(start)} – ${fmtMonthDay(end)}`
}

/** Engine returns either Date or ISO string after JSON round-trip; normalise. */
function toDate(value: Date | string | null | undefined): Date | null {
  if (!value) return null
  return value instanceof Date ? value : new Date(value)
}

interface CandidateWindow {
  window: SeedingWindow
  kind: "primary" | "secondary"
}

/**
 * Default soil-temp + germination copy by grass season. Falls back to
 * cool-season defaults when the result is missing the field.
 */
function seasonDefaults(season: "Cool" | "Warm"): { soilTemp: string; germDays: string } {
  if (season === "Warm") return { soilTemp: "65–85°F", germDays: "10–30 days" }
  return { soilTemp: "50–65°F", germDays: "7–14 days" }
}

/**
 * Per-grass overrides for soil temp + germination — keyed by the engine's
 * grass name. Values come from the same university extension data the
 * engine windows are tuned to.
 */
const GRASS_PROFILE: Record<string, { soilTemp: string; germDays: string }> = {
  "Tall Fescue": { soilTemp: "60–75°F", germDays: "7–14 days" },
  "Kentucky Bluegrass": { soilTemp: "55–70°F", germDays: "14–30 days" },
  "Perennial Ryegrass": { soilTemp: "55–75°F", germDays: "5–10 days" },
  "Fine Fescue": { soilTemp: "55–65°F", germDays: "10–14 days" },
  Bermudagrass: { soilTemp: "65–85°F", germDays: "10–30 days" },
  Zoysiagrass: { soilTemp: "70–85°F", germDays: "14–21 days" },
  Centipede: { soilTemp: "70–85°F", germDays: "14–28 days" },
  Bahiagrass: { soilTemp: "65–85°F", germDays: "14–28 days" },
  Buffalograss: { soilTemp: "65–80°F", germDays: "14–30 days" },
  "St. Augustinegrass": { soilTemp: "75–90°F", germDays: "Sodded only" },
  Kikuyugrass: { soilTemp: "65–80°F", germDays: "14–21 days" },
  "Annual Ryegrass": { soilTemp: "55–75°F", germDays: "5–10 days" },
  "Mixed Cool-Season Blend": { soilTemp: "55–75°F", germDays: "7–21 days" },
}

export function buildSeedingViewPlan(
  result: SeedingResult,
  today: Date = new Date(),
): SeedingViewPlan {
  const primary = result.primaryWindow
  const secondary = result.secondaryWindow

  const candidates: CandidateWindow[] = []
  if (primary) candidates.push({ window: primary, kind: "primary" })
  if (secondary) candidates.push({ window: secondary, kind: "secondary" })

  // Find a window where today is inside the optimal range, or just past optimal
  // but still inside the acceptable extension.
  let currentOptimal: CandidateWindow | null = null
  let currentAcceptable: CandidateWindow | null = null
  for (const c of candidates) {
    const optStart = toDate(c.window.optimalStart)
    const optEnd = toDate(c.window.optimalEnd)
    const accStart = toDate(c.window.acceptableStart)
    const accEnd = toDate(c.window.acceptableEnd)
    if (!optStart || !optEnd) continue
    if (today >= optStart && today <= optEnd) {
      currentOptimal = c
      break
    }
    if (
      !currentAcceptable &&
      accStart &&
      accEnd &&
      today > optEnd &&
      today <= accEnd
    ) {
      currentAcceptable = c
    }
  }

  // Upcoming windows sorted by start date.
  const upcoming = candidates
    .filter((c) => {
      const start = toDate(c.window.optimalStart)
      return start && today < start
    })
    .sort((a, b) => {
      const aStart = toDate(a.window.optimalStart)!.getTime()
      const bStart = toDate(b.window.optimalStart)!.getTime()
      return aStart - bStart
    })

  const next = upcoming[0] ?? null
  const profile = GRASS_PROFILE[result.grassType] ?? seasonDefaults(result.grassSeason)

  // Build calendar cells via the existing month-grid helper so the colors stay
  // in sync with the timeline.
  const calendar = buildSeedingCalendar(primary ?? null, secondary ?? null)
  const months = calendar?.cells ?? []
  const bestMonth = calendar?.bestMonth

  // ── Branch on verdict ──────────────────────────────────────────────
  if (currentOptimal) {
    const optEnd = toDate(currentOptimal.window.optimalEnd)!
    const daysLeft = Math.max(0, daysBetween(today, optEnd))
    if (daysLeft <= 10) {
      return {
        verdict: "closing",
        pillLabel: "Window closing",
        headline: "Seed this week — window closing",
        detail: `Only ${daysLeft} day${daysLeft === 1 ? "" : "s"} left in the ${
          currentOptimal.kind === "secondary" ? "spring backup" : "optimal"
        } window. Get seed down before ${fmtMonthDay(optEnd)}.`,
        ctaMode: "calendar",
        reminderDate: null,
        daysLeft,
        daysToOpen: null,
        bestWindow: currentOptimal.window,
        bestWindowKind: currentOptimal.kind,
        months,
        bestMonth,
        soilTemp: profile.soilTemp,
        germDays: profile.germDays,
      }
    }
    if (currentOptimal.kind === "secondary") {
      return {
        verdict: "go-secondary",
        pillLabel: "Good to seed",
        headline: "Good to seed — workable spring window",
        detail: `You're in the spring backup window. Best results are still in fall, but this works. ${daysLeft} days left in the window.`,
        ctaMode: "calendar",
        reminderDate: null,
        daysLeft,
        daysToOpen: null,
        bestWindow: currentOptimal.window,
        bestWindowKind: "secondary",
        months,
        bestMonth,
        soilTemp: profile.soilTemp,
        germDays: profile.germDays,
      }
    }
    return {
      verdict: "go",
      pillLabel: "Plant now",
      headline: "Plant now — you're in the peak window",
      detail: `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in the optimal window (closes ${fmtMonthDay(optEnd)}).`,
      ctaMode: "calendar",
      reminderDate: null,
      daysLeft,
      daysToOpen: null,
      bestWindow: currentOptimal.window,
      bestWindowKind: "primary",
      months,
      bestMonth,
      soilTemp: profile.soilTemp,
      germDays: profile.germDays,
    }
  }

  if (currentAcceptable) {
    const accEnd = toDate(currentAcceptable.window.acceptableEnd)!
    const daysLeft = Math.max(0, daysBetween(today, accEnd))
    return {
      verdict: "closing",
      pillLabel: "Window closing",
      headline: "Seed now — past optimal, still workable",
      detail: `You're in the acceptable buffer. ${daysLeft} day${daysLeft === 1 ? "" : "s"} until results drop sharply. Use a heavier seeding rate to compensate.`,
      ctaMode: "calendar",
      reminderDate: null,
      daysLeft,
      daysToOpen: null,
      bestWindow: currentAcceptable.window,
      bestWindowKind: currentAcceptable.kind,
      months,
      bestMonth,
      soilTemp: profile.soilTemp,
      germDays: profile.germDays,
    }
  }

  if (next) {
    const nextStart = toDate(next.window.optimalStart)!
    const daysToOpen = Math.max(0, daysBetween(today, nextStart))
    const reminderDate = new Date(nextStart.getTime() - 14 * DAY_MS)
    if (daysToOpen <= 21) {
      return {
        verdict: "soon",
        pillLabel: "Get ready",
        headline: `Get ready — opens in ${daysToOpen} day${daysToOpen === 1 ? "" : "s"}`,
        detail: `Sharpen the soil prep. Window opens ${fmtMonthDay(nextStart)}. Aerate or scratch the surface so seed has soil contact when it lands.`,
        ctaMode: "calendar",
        reminderDate: null,
        daysLeft: null,
        daysToOpen,
        bestWindow: next.window,
        bestWindowKind: next.kind,
        months,
        bestMonth,
        soilTemp: profile.soilTemp,
        germDays: profile.germDays,
      }
    }
    if (daysToOpen > 60) {
      return {
        verdict: "too-late",
        pillLabel: "Too late this season",
        headline: "Too late this season",
        detail: `The seeding window for this year has closed. Don't seed off-window — wait until ${fmtMonthDay(nextStart)} (${daysToOpen} days). Seeding now wastes seed.`,
        ctaMode: "remind",
        reminderDate,
        daysLeft: null,
        daysToOpen,
        bestWindow: next.window,
        bestWindowKind: next.kind,
        months,
        bestMonth,
        soilTemp: profile.soilTemp,
        germDays: profile.germDays,
      }
    }
    return {
      verdict: "wait",
      pillLabel: "Wait",
      headline: `Wait — window opens ${fmtMonthDay(nextStart)}`,
      detail: `${daysToOpen} days until the ${next.kind === "secondary" ? "spring backup" : "optimal"} window. Use the time to test soil and clear the area.`,
      ctaMode: "remind",
      reminderDate,
      daysLeft: null,
      daysToOpen,
      bestWindow: next.window,
      bestWindowKind: next.kind,
      months,
      bestMonth,
      soilTemp: profile.soilTemp,
      germDays: profile.germDays,
    }
  }

  return {
    verdict: "wait",
    pillLabel: "Wait",
    headline: "Wait",
    detail: "No upcoming seeding window found for this grass type and region.",
    ctaMode: "none",
    reminderDate: null,
    daysLeft: null,
    daysToOpen: null,
    bestWindow: null,
    bestWindowKind: null,
    months,
    bestMonth,
    soilTemp: profile.soilTemp,
    germDays: profile.germDays,
  }
}

// ── Grass picker config ─────────────────────────────────────────────

export type SeedingGrassSeason = "cool" | "warm"

export interface SeedingGrassCard {
  /** Display label used in the card body. */
  name: string
  /** Engine key (matches keys in GRASS_CHARACTERISTICS + seedingCalculatorSchema). */
  engineKey: string
  season: SeedingGrassSeason
  /** Short tag used as a hint, e.g. "Cool-season". */
  tag: string
  /** Soil-temp blurb shown under the name. */
  soilTemp: string
  /** Flagged when the grass is plug/sod only — no seeding rate. */
  soddedOnly?: boolean
}

export const SEEDING_GRASS_CARDS: SeedingGrassCard[] = [
  // Cool-season (first 4 are the "recommended" default set)
  {
    name: "Tall Fescue",
    engineKey: "Tall Fescue",
    season: "cool",
    tag: "Cool-season",
    soilTemp: "soil 60–75°F",
  },
  {
    name: "Kentucky Bluegrass",
    engineKey: "Kentucky Bluegrass",
    season: "cool",
    tag: "Cool-season",
    soilTemp: "soil 55–70°F",
  },
  {
    name: "Bermuda grass",
    engineKey: "Bermudagrass",
    season: "warm",
    tag: "Warm-season",
    soilTemp: "soil 65–85°F",
  },
  {
    name: "Zoysia grass",
    engineKey: "Zoysiagrass",
    season: "warm",
    tag: "Warm-season",
    soilTemp: "soil 70–85°F",
  },
  // Expansion set
  {
    name: "Perennial Ryegrass",
    engineKey: "Perennial Ryegrass",
    season: "cool",
    tag: "Cool-season",
    soilTemp: "soil 55–75°F",
  },
  {
    name: "Fine Fescue",
    engineKey: "Fine Fescue",
    season: "cool",
    tag: "Cool-season",
    soilTemp: "soil 55–65°F",
  },
  {
    name: "Centipede",
    engineKey: "Centipede",
    season: "warm",
    tag: "Warm-season",
    soilTemp: "soil 70–85°F",
  },
  {
    name: "Bahia grass",
    engineKey: "Bahiagrass",
    season: "warm",
    tag: "Warm-season",
    soilTemp: "soil 65–85°F",
  },
  {
    name: "St. Augustine",
    engineKey: "St. Augustinegrass",
    season: "warm",
    tag: "Warm-season · sodded",
    soilTemp: "plugs or sod only",
    soddedOnly: true,
  },
  {
    name: "Annual Ryegrass",
    engineKey: "Annual Ryegrass",
    season: "cool",
    tag: "Cool-season · overseed",
    soilTemp: "soil 55–75°F",
  },
  {
    name: "Cool-Season Mix",
    engineKey: "Mixed Cool-Season Blend",
    season: "cool",
    tag: "Cool-season",
    soilTemp: "soil 55–75°F",
  },
  {
    name: "Kikuyu grass",
    engineKey: "Kikuyugrass",
    season: "warm",
    tag: "Warm-season",
    soilTemp: "soil 65–80°F",
  },
]

/** First 4 cards are the always-visible "recommended" set. */
export const SEEDING_GRASS_DEFAULT_COUNT = 4

/** Look up a card by its engine key. */
export function findSeedingCard(engineKey: string): SeedingGrassCard | undefined {
  return SEEDING_GRASS_CARDS.find((c) => c.engineKey === engineKey)
}

/** Region default keys → engine grass keys. Falls back to Tall Fescue when
 *  the user's ZIP doesn't resolve to a known region. */
export const SEEDING_REGION_DEFAULTS: Record<string, string> = {
  Southeast: "Bermudagrass",
  "South Central": "Bermudagrass",
  Northeast: "Tall Fescue",
  "Great Lakes": "Kentucky Bluegrass",
  "Upper Midwest": "Kentucky Bluegrass",
  "Mountain West": "Kentucky Bluegrass",
  "Mid-Atlantic": "Tall Fescue",
  Central: "Tall Fescue",
  "West Coast": "Tall Fescue",
}
